// ─── The lens power grid ─────────────────────────────────────────────────────
//
// One definition of the SPH/CYL axes, shared by every screen that draws a lens
// matrix: the store stock table, the order pad, and central's stock requests.
//
// It lives here because these had drifted apart once already — a table built on
// its own axis list stopped lining up with the others, and stray powers from
// stored data leaked in as blank rows. Anything that plots a lens power should
// import from here rather than rebuilding the range.
//
// SPH +6.00 … -6.00 and CYL 0 … 4.00, both in 0.25 steps.
//
// SPH runs positive-first (plus at the top, minus at the bottom) to match how
// staff read the printed stock sheet. The order here is the render order for
// every grid — everything else that touches this axis uses it for set
// membership or an order-preserving filter, so flipping it is display-only.

export const LENS_SPH_VALUES = Array.from({ length: 49 }, (_, i) => Number((6 - (i * 0.25)).toFixed(2)));
export const LENS_CYL_VALUES = Array.from({ length: 17 }, (_, i) => Number((i * 0.25).toFixed(2)));

// Cell keys are fixed-decimal strings ("-1.25") so they match the server, which
// builds its matrix keys with the same toFixed(2).
export const formatDecimalKey = (value) => Number(value).toFixed(2);

// Column headings read better without trailing zeros: "-1.5", not "-1.50".
// Cylinder is kept as a magnitude in stock (the axis above is 0 … 4), but the
// sheet is minus-cylinder, so headings carry the sign staff write on an Rx.
export const formatCylLabel = (value) => (Number(value) === 0 ? '0' : `-${Math.abs(Number(value))}`);

// Background banding by power range — the visual grouping staff navigate by.
export const zoneBg = (sph, cyl) => {
  const s = Number(sph);
  const c = Number(cyl);
  if (s <= -6.5) return '#bbf7d0';
  if (s <= -4.25) return '#f5d0fe';
  if (s <= -2.75) return '#bae6fd';
  if (s <= -1.25) return '#dcfce7';
  if (c >= 2.5) return '#ecfccb';
  return '#ffffff';
};

// Cell geometry, so every lens grid is the same size wherever it is drawn.
// Kept compact so a full 49×17 matrix needs as little scrolling as possible —
// staff read these as whole sheets, not cell by cell. Font sizes in the tables
// are tuned to these numbers, so scale both together.
export const LENS_CELL_W = 30;
export const LENS_CELL_H = 17;
export const LENS_SPH_COL_W = 40;
